import type { RegistryItemFragment } from '@graphql/generated';
import React from 'react';
import { Box, ButtonV2, DrawerV2, Flex, TextV2, Spinner } from '@withjoy/joykit';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useAutoDismissUpdateConfirmation } from './useAutoDismissUpdateConfirmation';
import { useUpdateForm } from './useUpdateForm';
import { useDrawerVisibilty } from './useDrawerVisibilty';
import { RegistryItemDisplayDetails } from './components/RegistryItemDisplayDetails/RegistryItemDisplayDetails';
import { useTranslation } from '@shared/core';
import { useCatalogLoggingContext } from '../../Catalog.logging';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { Page } from '../../../RegistryGiftCounts/telemetry';
import { useEventInfo } from '@shared/utils/eventInfo';
import { ReactComponent as HeartSvg } from '@assets/icons/icon-heart-white.svg';
import { ReactComponent as BlackHeartSvg } from '@assets/icons/icon-heart-filled.svg';
import { Confetti } from '@shared/components/Confetti';
import { pxToRem } from '@withjoy/joykit/theme';
import { RegistryAdvisorMobile } from '../../../RegistryGiftCounts/RegistryAdvisorMobile';

interface RecentlyAddedRegistryItemUpdateDrawerProps {
  item: RegistryItemFragment;
  closeDrawer: () => void;
}

const ConfirmationBanner: React.FC = ({ children }) => {
  return (
    <Box
      background="black"
      display="flex"
      alignItems="center"
      justifyContent="center"
      color="white"
      paddingX={6}
      paddingY={5}
    >
      <TextV2 typographyVariant="body1">{children}</TextV2>
    </Box>
  );
};

const Root = (props: RecentlyAddedRegistryItemUpdateDrawerProps) => {
  const { item, closeDrawer } = props;

  const { showConfirmationBanner, shouldShowConfirmationBanner } = useAutoDismissUpdateConfirmation();
  const { toggleRecentlyAddedRegistryItemUpdateDrawerSetting, saveRecentlyAddedRegistryItemUpdateDrawerSettings } = useCatalogLoggingContext();
  const { eventInfo, eventHandle } = useEventInfo();
  const handleOnUpdateItem = useEventCallback(() => {
    saveRecentlyAddedRegistryItemUpdateDrawerSettings({});
    showConfirmationBanner();
  });

  const { formik, isUpdatingRegistryItem } = useUpdateForm({ itemId: item.id, onUpdate: handleOnUpdateItem });

  const { t } = useTranslation('catalogRegistry');

  const translations = t('recentlyAddedRegistryItemUpdateDrawer');

  const [buttonLabel, setButtonLabel] = React.useState(translations.updateCtaMessage());

  const [isMostWanted, setIsMostWanted] = React.useState(false);

  const handleButtonClick = () => {
    const newState = !isMostWanted;

    toggleRecentlyAddedRegistryItemUpdateDrawerSetting({
      name: 'RecentlyAddedRegistryItemUpdateDrawer.markAsMostWanted',
      value: newState,
    });

    formik.setFieldValue('markAsMustHave', newState);

    setIsMostWanted(newState);
    setButtonLabel(newState ? translations.mostWanted() : translations.updateCtaMessage());
  };

  const handleMouseEnter = () => {
    if (isMostWanted) {
      setButtonLabel(translations.removeFromMostWanted());
    }
  };

  const handleMouseLeave = () => {
    setButtonLabel(isMostWanted ? translations.mostWanted() : translations.updateCtaMessage());
  };

  return (
    <>
      {shouldShowConfirmationBanner && <ConfirmationBanner>{translations.updateConfirmationMessage()}</ConfirmationBanner>}
      <DrawerV2.Body height="auto">
        <Box
          position="absolute"
          width="100%"
          height="100%"
          top={0}
          left={0}
          pointerEvents="none"
          overflow="hidden"
        >
          <Confetti />
        </Box>
        <form onSubmit={formik.handleSubmit}>
          <Flex
            rowGap={8}
            flexDirection="column"
          >
            <RegistryItemDisplayDetails item={item} />
            <ButtonV2
              color={isMostWanted && !isUpdatingRegistryItem ? 'mono14' : 'white'}
              borderColor="mono14"
              intent="neutral"
              borderRadius={24}
              size={'md'}
              padding={[4, 24]}
              _hover={
                isMostWanted
                  ? {
                      color: 'mono12',
                      borderColor: 'mono12',
                      backgroundColor: 'buttonOutlineNeutralFillHover',
                    }
                  : {
                      color: 'white',
                      backgroundColor: 'mono11',
                    }
              }
              _active={{ color: 'mono14' }}
              variant={isMostWanted && !isUpdatingRegistryItem ? 'outline' : 'solid'}
              startIcon={isMostWanted && !isUpdatingRegistryItem ? <BlackHeartSvg /> : <HeartSvg />}
              type="submit"
              shape="rounded"
              onClick={handleButtonClick}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              loading={isUpdatingRegistryItem}
            >
              {buttonLabel}
            </ButtonV2>
          </Flex>
        </form>
        <Flex marginTop={40}>
          <RegistryAdvisorMobile
            page={Page.addItemDrawer}
            eventId={eventInfo?.eventId || ''}
            eventHandle={eventHandle || ''}
            onActionComplete={closeDrawer}
          />
        </Flex>
      </DrawerV2.Body>
    </>
  );
};

const LoadingState: React.FC = () => {
  const { t } = useTranslation('catalogRegistry');
  const translations = t('recentlyAddedRegistryItemUpdateDrawer');

  return (
    <DrawerV2.Body height="auto">
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        padding={8}
        minHeight="200px"
      >
        <Box marginBottom={4}>
          <Spinner />
        </Box>
        <TextV2
          typographyVariant="body1"
          textAlign="center"
        >
          {translations.loadingMessage()}
        </TextV2>
      </Flex>
    </DrawerV2.Body>
  );
};

export const RecentlyAddedRegistryItemUpdateDrawer = () => {
  const isMobile = useIsMobileScreen();

  const { currentItem, isOpen, isAddingItem, onClose } = useDrawerVisibilty();
  const { t } = useTranslation('catalogRegistry');

  return (
    <DrawerV2
      isOpen={isOpen}
      onClose={onClose}
      anchor={isMobile ? 'bottom' : 'right'}
      size={isMobile ? 'auto' : undefined}
      overrides={{
        CloseButton: {
          props: {
            top: '50%',
            transform: 'translateY(-50%)',
            right: 6,
            padding: 0,
            borderRadius: 'full',
            border: '1px solid',
            borderColor: 'mono5',
            height: 8,
            width: 8,
            minWidth: 'unset',
            color: 'black',
          },
        },
        Content: {
          props: {
            width: ['100%', pxToRem(423)],
          },
        },
      }}
    >
      <DrawerV2.Header
        borderBottom="1px solid"
        borderBottomColor={'mono3'}
        typographyVariant="hed3"
      >
        {t('recentlyAddedRegistryItemUpdateDrawer', 'drawerTitle')()}
        <DrawerV2.CloseButton />
      </DrawerV2.Header>
      {isAddingItem ? (
        <LoadingState />
      ) : (
        currentItem && (
          <Root
            item={currentItem}
            closeDrawer={onClose}
          />
        )
      )}
    </DrawerV2>
  );
};
